import { useMemo } from "react";
import dayjs from "dayjs";
import { useTranslations } from "use-intl";
import { flightFormValuesSchema } from "@/app/ui/flights/validation";
import { useValidatedLocalStorage } from "./useValidatedLocalStorage";
import type { TFlightFormFieldValues } from "../types/types";
import { initialFlightsValues } from "../initialFlightsValues";
import type { THotelFormFieldValues } from "@/features/hotels/types/types";
import { initialHotelsValues } from "@/features/hotels/utils/initialHotelsValues";
import { getHotelFormValuesSchema } from "@/features/hotels/components/hotels/validation";

export const HOTELS_SEARCH_DATA_STORAGE_KEY = "tz_previousHotelSearchData@2";
export const FLIGHTS_SEARCH_DATA_STORAGE_KEY = "tz_previousFlightSearchData@2";

const createTravelDates = (startDate: Date, endDate?: Date) => {
  // if dates are in the past, create new ones based on travel duration
  if (dayjs(startDate).isBefore(new Date(), "day")) {
    const duration = endDate ? dayjs(endDate).diff(startDate, "day") || 1 : 1;
    return [new Date(), dayjs().add(duration, "day").toDate()];
  }

  // if dates are valid, use them
  return [startDate, endDate || startDate];
};

const createHotelTravelDates = (startDate: Date, endDate: Date) =>
  createTravelDates(startDate, endDate);
const createFlightTravelDates = (startDate: Date, endDate?: Date) =>
  createTravelDates(startDate, endDate);

export const usePreviousHotelSearchData = () => {
  const t = useTranslations();
  const [previousSearchData, setPreviousSearchData] =
    useValidatedLocalStorage<THotelFormFieldValues>(
      HOTELS_SEARCH_DATA_STORAGE_KEY,
      initialHotelsValues,
      getHotelFormValuesSchema(t),
    );

  const normalizedSearchData = useMemo(() => {
    const searchData = previousSearchData || initialHotelsValues;

    const [checkIn, checkOut] = createHotelTravelDates(
      searchData.dates.checkIn,
      searchData.dates.checkOut,
    );

    return {
      ...searchData,
      dates: {
        checkIn,
        checkOut,
      },
    };
  }, [previousSearchData]);

  return {
    previousSearchData: normalizedSearchData,
    setPreviousSearchData,
  };
};

export const usePreviousFlightSearchData = () => {
  const t = useTranslations();

  const initialValues = useMemo(() => {
    const defaultDeparture = {
      id: "1",
      name: t("searchRecommendations.baghdad"),
      code: "BGW",
      countryName: t("searchRecommendations.iraq"),
      countryCode: "IQ",
      type: "CITY" as const,
    };

    return { ...initialFlightsValues, from: defaultDeparture };
  }, [t]);

  const [previousSearchData, setPreviousSearchData] =
    useValidatedLocalStorage<TFlightFormFieldValues>(
      FLIGHTS_SEARCH_DATA_STORAGE_KEY,
      initialValues,
      flightFormValuesSchema,
    );

  const normalizedSearchData = useMemo(() => {
    const searchData = previousSearchData || initialValues;

    const [departure, returnDate] = createFlightTravelDates(
      searchData.dates.departure,
      searchData.dates.return,
    );

    return {
      ...searchData,
      dates: {
        departure,
        return: returnDate,
      },
    };
  }, [initialValues, previousSearchData]);

  return {
    previousSearchData: normalizedSearchData,
    setPreviousSearchData,
  };
};
